// owner/ui/BookingRow.js — one booking as a row, in three shapes (approved mock-ups):
//   arrival   — unit, code + platform, check-in → check-out, nights;
//   departure — unit, code + platform, «تسجيل خروج» and the date (no time: the only time on record is the unit's
//               default, not the guest's — Adil, 4 Nov 2026);
//   booking   — the bookings list row (bookings-phone mock-up), used on /bookings AND a unit's bookings tab: guest,
//               unit and code (`withUnit`; left out on the unit's own tab), status; dates, nights and the amount.
//               A cancelled booking is faded with its amount struck through.
// Rows read the owner allow-listed booking (be/routes/bookingRoutes.js decorateBookingForOwner), `nights` included.

import React from 'react';
import { Link } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import Icon from './Icon';
import PlatformMark from './PlatformMark';
import { UnitThumb } from './UnitBits';
import useOwnerFormat from './useOwnerFormat';

/** Booking status → chip colour. One map for every owner screen that shows a booking's status. */
export const BOOKING_STATUS_CHIP = { upcoming: 'upcoming', active: 'staying', completed: 'done', cancelled: 'cancelled' };

export const BookingStatusChip = ({ status }) => {
  const { t } = useLanguage();
  if (!(status in BOOKING_STATUS_CHIP)) return null;
  return <span className={`o-chip ${BOOKING_STATUS_CHIP[status]}`}>{t(`ownerApp.bookingStatus.${status}`)}</span>;
};

// `withYear`: the year after the check-out date. The bookings list spans every year (latest check-in first), so
// without it a stay in July 2027 reads as last July (Adil, 4 Nov 2026). The overview's 7-day rows leave it out.
const DatePair = ({ booking, withYear = false }) => {
  const f = useOwnerFormat();
  return (
    <span className="o-date-pair">
      {f.dayMonth(booking.checkIn)} <Icon name="arrowTo" size="xs" /> {withYear ? f.date(booking.checkOut) : f.dayMonth(booking.checkOut)}
    </span>
  );
};

/** The list row (`variant="booking"`). The amount shows only when one is recorded — never a made-up zero. */
const ListRow = ({ booking, withUnit, photo, index }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const unit = booking.property || {};
  const cancelled = booking.status === 'cancelled';
  const hasAmount = Number(booking.totalAmount) > 0;
  return (
    <li className={`o-bk${cancelled ? ' cancelled' : ''}`}>
      <Link to={`/bookings/${booking._id}`} className="o-bk-link">
        <div className="o-bk-top">
          {withUnit && (
            <span className="o-house-wrap"><UnitThumb photo={photo} index={index} /><PlatformMark platform={booking.sourcePlatform} /></span>
          )}
          <div className="o-body">
            <p className="o-t">{booking.guestName || t('ownerApp.unit.guestFallback')}</p>
            <p className="o-m">
              {withUnit
                ? <>{unit.name}{unit.propertyCode && <span className="o-code">{unit.propertyCode}</span>}</>
                : <PlatformMark platform={booking.sourcePlatform} withName />}
            </p>
          </div>
          <BookingStatusChip status={booking.status} />
        </div>
        <div className="o-bk-foot">
          <span>
            <DatePair booking={booking} withYear />
            {Number.isInteger(booking.nights) && <span className="o-nights">{f.nights(booking.nights)}</span>}
          </span>
          {hasAmount && (
            <span className="o-amount">
              <span className="o-money">{f.amount(booking.totalAmount)}</span> <span className="o-cur">{f.currency}</span>
            </span>
          )}
        </div>
      </Link>
    </li>
  );
};

const BookingRow = ({ booking, variant = 'arrival', photo, index = 0, withUnit = true }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  if (variant === 'booking') return <ListRow booking={booking} withUnit={withUnit} photo={photo} index={index} />;

  const unit = booking.property || {};
  const nights = Number.isInteger(booking.nights) ? f.nights(booking.nights) : null;
  return (
    <li>
      <Link to={`/bookings/${booking._id}`} className="o-row">
        <UnitThumb photo={photo} index={index} />
        <div className="o-body">
          <p className="o-t">{unit.name}</p>
          <p className="o-m">
            {unit.propertyCode && <span className="o-code">{unit.propertyCode}</span>}
            <PlatformMark platform={booking.sourcePlatform} withName />
          </p>
          {variant === 'arrival' && <p className="o-m"><DatePair booking={booking} /></p>}
        </div>
        {variant === 'departure' ? (
          <div className="o-end o-when">
            <span className="o-lbl">{t('ownerApp.common.checkOut')}</span>
            <span className="o-dt">{f.dayMonth(booking.checkOut)}</span>
          </div>
        ) : (
          <div className="o-end">{nights && <span className="o-chip upcoming">{nights}</span>}</div>
        )}
      </Link>
    </li>
  );
};

export default BookingRow;
